import { InformationAlertIcon } from "@/assets/images/svgIcons/informationAlertIcon"
import { Button } from "@/components/ui/baseUi/button"
import { Checkbox } from "@/components/ui/baseUi/checkbox"
import { CalendarInput } from "@/components/ui/forms/calendarInput"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { SwitchInlineInput } from "@/components/ui/forms/switchInlineInput"
import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import type { UserProgramDetailsEntity } from "@/modules/programs/entities/userProgramDetailsEntity"
import { ProgramType } from "@/modules/programs/types/programTypes"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { AddBlock } from "@/modules/sessions/sessionForm/blocks/components/addBlock"
import { ExistingBlock } from "@/modules/sessions/sessionForm/blocks/components/existingBlock"
import type { ExerciseFormikType } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import type { MediaBlockFormikType } from "@/modules/sessions/sessionForm/blocks/mediaBlock/mediaBlockFormikTypes"
import type {
  AthleteNoteFormikType,
  CoachNoteFormikType,
} from "@/modules/sessions/sessionForm/blocks/noteBlock/noteFormikTypes"
import { ProgramSessionFormHeader } from "@/modules/sessions/sessionForm/components/programSessionFormHeader"
import { SessionTagEnum, SessionTagInput } from "@/modules/sessions/sessionForm/components/sessionTagInput"
import { useBlockDuration } from "@/modules/sessions/sessionForm/hooks/useBlockDuration"
import { useProgramInfiniteOrProSessionFormik } from "@/modules/sessions/sessionForm/hooks/useProgramInfiniteOrProSessionFormik"
import { useSessionSidebar } from "@/modules/sessions/sessionForm/hooks/useSessionSidebar"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import { useTemplateExerciseImportSidebar } from "@/modules/template/hooks/useTemplateSidebar"
import { HUCardAlert } from "@/modules/ui/components/huCardAlert"
import { useConfirmPopup } from "@/modules/ui/components/popup/huConfirmPopup"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { displayedFullDateFormat } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { BookTextIcon, LoaderCircle } from "lucide-react"
import { useEffect, useRef } from "react"

type ProgramInfiniteOrProSessionFormProps = {
  program: UserProgramDetailsEntity
  editedSession?: ProgramInfiniteSessionEntity
  dateSession?: string
  onFinish: () => void
  className?: string
} & React.ComponentProps<"div">

export function ProgramInfiniteOrProSessionForm({
  program,
  editedSession,
  dateSession,
  onFinish,
  className,
  ...props
}: ProgramInfiniteOrProSessionFormProps) {
  const { navigateToExerciseForm, navigateToNoteForCoachesForm, navigateToNoteForAthletesForm, navigateToMediaForm } =
    useSessionSidebar()
  const { hideSidebarStackOnTop, setCurrentStackOnHide } = useSidebar()
  const { navigateToTemplateExerciseImportView } = useTemplateExerciseImportSidebar()
  const confirmPopup = useConfirmPopup()
  const containerRef = useRef<HTMLDivElement>(null)
  const { formik, isSubmitting } = useProgramInfiniteOrProSessionFormik({
    program,
    editedSession,
    dateSession,
  })
  const { timedBlocks, timedBlocksTotal } = useBlockDuration(formik.values.blocks)

  useEffect(() => {
    setCurrentStackOnHide(async () => {
      if (formik.dirty) {
        const confirm = await new Promise<boolean>((resolve) => {
          confirmPopup.show({
            title: word("program.sidebar.closeMessage.title"),
            message: word("program.sidebar.closeMessage.message"),
            accept: () => {
              resolve(true)
              return Promise.resolve()
            },
            cancel: () => {
              resolve(false)
              return Promise.resolve()
            },
            footerProps: {
              confirmText: word("global.ok"),
            },
          })
        })

        return confirm
      } else {
        return true
      }
    })
  }, [formik.dirty])

  const onSubmitSessionForm = async () => {
    const isOnOffRestDay = "restDay" in formik.values && formik.values.restDay
    const hasNoBlocks = formik.values.blocks.length === 0

    if (hasNoBlocks && isOnOffRestDay === false) {
      toast.error(word("program.session.blockRequiredError"), {
        classNames: errorToastClassName(),
      })
      return
    }

    Object.keys(formik.values).forEach((inputKey) => {
      void formik.setFieldTouched(inputKey)
    })
    const errors = await formik.validateForm()

    if (Object.keys(errors).length === 0) {
      await formik.submitForm()
      onFinish()
    } else {
      const errorElements = containerRef.current?.querySelectorAll('[aria-invalid="true"]')
      if (errorElements && errorElements.length > 0) {
        document
          .querySelector('[data-slot="right-sidebar-content"]')
          ?.scrollTo({ top: (errorElements[0] as HTMLElement).offsetTop - 30, behavior: "smooth" })
      }
    }
  }

  const addExercise = () => {
    navigateToExerciseForm(undefined, formik.values.blocks.length, async (exercise: ExerciseFormikType) => {
      await formik.setFieldValue("blocks", [
        ...formik.values.blocks,
        { type: SessionBlockType.SESSION_EXERCISE, content: exercise },
      ])
      hideSidebarStackOnTop()
    })
  }

  const addCoachNote = () => {
    navigateToNoteForCoachesForm(undefined, formik.values.blocks.length, async (note: CoachNoteFormikType) => {
      await formik.setFieldValue("blocks", [
        ...formik.values.blocks,
        { type: SessionBlockType.COACH_NOTE, content: note },
      ])
      hideSidebarStackOnTop()
    })
  }

  const addAthleteNoteBlock = () => {
    navigateToNoteForAthletesForm(undefined, formik.values.blocks.length, async (note: AthleteNoteFormikType) => {
      await formik.setFieldValue("blocks", [
        ...formik.values.blocks,
        { type: SessionBlockType.ATHLETE_NOTE, content: note },
      ])
      hideSidebarStackOnTop()
    })
  }
  const addMediaBlock = () => {
    navigateToMediaForm(undefined, formik.values.blocks.length, async (mediaBlock: MediaBlockFormikType) => {
      await formik.setFieldValue("blocks", [
        ...formik.values.blocks,
        { type: SessionBlockType.MEDIAS, content: mediaBlock },
      ])
      hideSidebarStackOnTop()
    })
  }

  const addTemplateExercise = () => {
    navigateToTemplateExerciseImportView(formik.values.blocks.length, async (exercise: ExerciseFormikType) => {
      await formik.setFieldValue("blocks", [
        ...formik.values.blocks,
        { type: SessionBlockType.SESSION_EXERCISE, content: exercise },
      ])
      hideSidebarStackOnTop()
    })
  }

  return (
    <div ref={containerRef} className={cn("flex flex-col px-6 pt-18 pb-12", className)} {...props}>
      <div className="flex flex-col pb-6" style={{ gap: 30 }}>
        <ProgramSessionFormHeader isCreation={!editedSession} formik={formik} />

        {program.type === ProgramType.PRO && (
          <HUCardAlert
            className="rounded-2xl"
            title={word("sessionForm.create.pro.oneSessionPerDay.title")}
            description={word("sessionForm.create.pro.oneSessionPerDay.content")}
            icon={
              <InformationAlertIcon
                backgroundColor="var(--primary-500)"
                color="var(--textDefault-body)"
                className="shrink-0"
              />
            }
          />
        )}

        <div className="flex flex-col gap-8">
          {/* Session name */}
          <StandardInput
            isRequiredInput
            label={word("sessionForm.name.title")}
            getFieldProps={formik.getFieldProps}
            setFieldValue={formik.setFieldValue}
            error={getFormErrorMessage("name", formik)}
            name="name"
          />

          {/* Session date */}
          <CalendarInput
            label={word("sessionForm.date.title")}
            name="date"
            isRequiredInput
            getFieldProps={formik.getFieldProps}
            error={getFormErrorMessage("date", formik, { errorMessage: word("sessionForm.error.date") })}
            setFieldValue={formik.setFieldValue}
            calendarProps={{
              minDate: dayjs().toDate(),
              maxDate: dayjs().add(1, "year").toDate(),
              dateFormat: displayedFullDateFormat,
            }}
          />

          {/* Session tag selection */}
          <div className="flex flex-col gap-2">
            <Text fontStyle="LS" className="text-textDefault-body uppercase">
              {word("sessionForm.tag.title")}
            </Text>

            <div className="flex flex-row items-center justify-between">
              <SessionTagInput
                selectedTags={[
                  { key: SessionTagEnum.gymnasticTag, value: formik.values.gymnasticTag },
                  { key: SessionTagEnum.weightliftingTag, value: formik.values.weightliftingTag },
                  { key: SessionTagEnum.cardioTag, value: formik.values.cardioTag },
                ]}
                onChangeSelectedTags={(tag: string, value: boolean) => {
                  void formik.setFieldValue(tag, value)
                }}
              />
            </div>
          </div>

          {/* Blocks */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center justify-between">
              <Text fontStyle="LS" className="text-textDefault-body uppercase">
                {`${word("program.planning.create.exercise.block_plural", { count: formik.values.blocks.length })} (${formik.values.blocks.length.toString()})`}
              </Text>

              {timedBlocks.length > 0 && (
                <div className="flex flex-row">
                  <Text fontStyle="LS" className="border-r border-borderDefault pr-1 text-textDefault-body uppercase">
                    {`${word("sessionForm.blocks.timed")} (${timedBlocks.length.toString()})`}
                  </Text>
                  <Text fontStyle="LS" className="pl-1 text-textDefault-body">
                    {word("sessionForm.block.totalTime")}
                    <Text fontStyle="LS" className="pl-1 font-bold">
                      {timedBlocksTotal}
                    </Text>
                    {word("global.minute.shortLabel", { count: timedBlocksTotal })}
                  </Text>
                </div>
              )}
            </div>

            {formik.values.blocks.map((block, i) => (
              <ExistingBlock formik={formik} block={block} blockIndex={i} key={i} />
            ))}

            {/* Add block */}
            <AddBlock
              onAddExerciseBlock={addExercise}
              onAddCoachNoteBlock={addCoachNote}
              onAddAthleteNoteBlock={addAthleteNoteBlock}
              onAddMediaBlock={addMediaBlock}
              onAddTemplateExercise={addTemplateExercise}
            />
          </div>

          {/* Hide session */}
          <SwitchInlineInput
            label={word("session.form.hideSessionContent.label")}
            name="hideSessionContent"
            isRequiredInput
            setFieldValue={formik.setFieldValue}
            getFieldProps={formik.getFieldProps}
            error={getFormErrorMessage("hideSessionContent", formik)}
            textSwitch={word("session.form.hideSessionContent.textSwitch")}
          >
            <Text fontStyle="BS" className="px-8 text-justify leading-5 text-textDefault-caption">
              {word("session.form.hideSessionContent.description")}
            </Text>
          </SwitchInlineInput>

          <div className="flex justify-between rounded-3xl border border-surface-border-gray-muted p-6">
            <div className="flex flex-row items-center gap-2">
              <BookTextIcon />
              <Text fontStyle="LL">{word("session.form.saveAsTemplate.button")}</Text>
            </div>
            <Checkbox
              size="lg"
              checked={formik.values.isTemplateSaved}
              onCheckedChange={() => void formik.setFieldValue("isTemplateSaved", !formik.values.isTemplateSaved)}
            />
          </div>
        </div>

        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={() => void onSubmitSessionForm()}
          disabled={isSubmitting}
          isLoading={isSubmitting}
        >
          {isSubmitting && <LoaderCircle className="animate-spin" />}
          {word("box.slot.form.submit.button")}
        </Button>
      </div>
    </div>
  )
}
